import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/apiService';
import { Tender, Contract, TenderPerformance, TenderStatus } from '@/types';
import { toFiniteNumber } from '@/utils/money';

// ── Tipos computados ────────────────────────────────────────────────────
export interface Kpi {
  value: number;
  /** Para exibição (ex: "R$ 2,84M" ou "31%") */
  display: string;
  change?: number;
  series?: number[];
}

export interface DashboardKpis {
  editaisAcompanhando: Kpi;
  licitacoesGanhasYtd: Kpi;
  taxaConversao: Kpi;
  contratosAtivos: Kpi;
}

export interface PipelineStage {
  key: string;
  label: string;
  count: number;
  total: number;
  pct: number; // 0..100
}

export interface PrazoCritico {
  id: string | number;
  targetId?: string | number;
  titulo: string;
  subtitulo?: string;
  dataLimite: Date;
  valor?: number;
  route?: string;
  tipo: string; // 'tender' | 'contract' | 'order' | 'communication' | 'document' | 'portal'
}

export interface InsightIA {
  tone: 'warning' | 'info' | 'success' | 'danger';
  badgeLabel: string;
  title: string;
  text: string;
  cta: string;
  action?: string; // rota destino ex: '/atestados'
}

export interface AtividadeItem {
  id: string | number;
  userInitials: string;
  userName: string;
  isAI: boolean;
  action: string;
  target: string;
  createdAt: Date;
}

export interface DashboardData {
  kpis: DashboardKpis;
  pipeline: PipelineStage[];
  prazosCriticos: PrazoCritico[];
  insights: InsightIA[];
  atividade: AtividadeItem[];
  totalPrazos7d: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────
export function formatCompactBRL(value: number): string {
  if (!isFinite(value) || value === 0) return 'R$ 0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(2).replace('.', ',')}M`;
  if (abs >= 1_000) return `R$ ${(value / 1_000).toFixed(0)}K`;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

function sampleSeries(values: number[], buckets = 12): number[] {
  if (values.length === 0) return Array(buckets).fill(0);
  if (values.length <= buckets) return [...values, ...Array(buckets - values.length).fill(values[values.length - 1] || 0)];
  const step = values.length / buckets;
  return Array.from({ length: buckets }, (_, i) => values[Math.floor(i * step)] ?? 0);
}

/** Série sintética crescente/decrescente baseada no valor final e na variação %. */
function syntheticSeries(finalValue: number, changePct: number, buckets = 12): number[] {
  if (!isFinite(finalValue) || finalValue === 0) return Array(buckets).fill(0);
  const initial = finalValue / (1 + changePct / 100);
  return Array.from({ length: buckets }, (_, i) => {
    const t = i / (buckets - 1);
    const noise = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * 0.04;
    return initial + (finalValue - initial) * t * (1 + noise);
  });
}

// ── Status groups (alinhados a types.ts e TenderList) ───────────────────
const PIPELINE_STATUSES: TenderStatus[] = [
  TenderStatus.OPEN,
  TenderStatus.ANALYSIS,
  TenderStatus.PARTICIPATE,
  TenderStatus.HIGH_PRIORITY,
  TenderStatus.EXTREME_PRIORITY,
];

const ONGOING_STATUSES: TenderStatus[] = [
  TenderStatus.PARTICIPATE,
  TenderStatus.HIGH_PRIORITY,
  TenderStatus.EXTREME_PRIORITY,
];

// ── Hook principal ──────────────────────────────────────────────────────
export function useDashboard() {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [agenda, setAgenda] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [performance, setPerformance] = useState<TenderPerformance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [t, c, a, logs, perf] = await Promise.allSettled([
        apiService.getTenders(),
        apiService.getContracts().catch(() => []),
        apiService.getAgenda().catch(() => []),
        apiService.getAuditLogs?.(undefined, 20).catch(() => []) ?? Promise.resolve([]),
        apiService.getTenderPerformance(365).catch(() => null),
      ]);
      setTenders(t.status === 'fulfilled' ? (t.value as Tender[]) : []);
      setContracts(c.status === 'fulfilled' ? (c.value as Contract[]) : []);
      setAgenda(a.status === 'fulfilled' ? ((a.value as any[]) || []) : []);
      setAuditLogs(logs.status === 'fulfilled' ? ((logs.value as any[]) || []) : []);
      setPerformance(perf.status === 'fulfilled' ? (perf.value as TenderPerformance | null) : null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const data = useMemo<DashboardData>(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    // ── KPIs ──
    const editaisAcomp = tenders.filter(t => ONGOING_STATUSES.includes(t.status as TenderStatus));
    const ganhasYtd = tenders.filter(t => {
      if (t.status !== TenderStatus.WON) return false;
      const d = t.deadline ? new Date(t.deadline) : null;
      return d && d.getFullYear() === currentYear;
    });
    const valorGanhoYtd = ganhasYtd.reduce((sum, t) => sum + toFiniteNumber(t.value), 0);

    const decididas = tenders.filter(t => t.status === TenderStatus.WON || t.status === TenderStatus.LOST);
    const taxaConv = decididas.length > 0
      ? (tenders.filter(t => t.status === TenderStatus.WON).length / decididas.length) * 100
      : 0;

    const contratosAtivos = contracts.filter(c => {
      if (!c.endDate) return true;
      const end = new Date(c.endDate);
      return end.getTime() > now.getTime();
    });

    const kpis: DashboardKpis = {
      editaisAcompanhando: {
        value: performance?.summary.activePortfolio ?? editaisAcomp.length,
        display: String(performance?.summary.activePortfolio ?? editaisAcomp.length),
      },
      licitacoesGanhasYtd: {
        value: performance?.summary.wonValue ?? valorGanhoYtd,
        display: formatCompactBRL(performance?.summary.wonValue ?? valorGanhoYtd),
      },
      taxaConversao: {
        value: performance?.summary.winRate ?? taxaConv,
        display: `${Math.round(performance?.summary.winRate ?? taxaConv)}%`,
      },
      contratosAtivos: {
        value: contratosAtivos.length,
        display: String(contratosAtivos.length),
      },
    };

    // ── Pipeline ──
    const totalPorStatus = (s: TenderStatus) => {
      const list = tenders.filter(t => t.status === s);
      return { count: list.length, total: list.reduce((a, b) => a + toFiniteNumber(b.value), 0) };
    };
    const localPipelineRaw = [
      { key: 'ANALYSIS',         label: 'Análise de edital',     ...totalPorStatus(TenderStatus.ANALYSIS) },
      { key: 'OPEN',             label: 'Proposta em preparação',...totalPorStatus(TenderStatus.OPEN) },
      { key: 'PARTICIPATE',      label: 'Em disputa',            ...totalPorStatus(TenderStatus.PARTICIPATE) },
      { key: 'HIGH_PRIORITY',    label: 'Habilitação',           ...totalPorStatus(TenderStatus.HIGH_PRIORITY) },
      { key: 'EXTREME_PRIORITY', label: 'Adjudicado',            ...totalPorStatus(TenderStatus.EXTREME_PRIORITY) },
    ];
    const pipelineRaw = performance?.funnel.map((stage) => ({
      key: stage.key,
      label: stage.label,
      count: stage.count,
      total: stage.value,
    })) || localPipelineRaw;
    const maxCount = Math.max(...pipelineRaw.map(p => p.count), 1);
    const pipeline: PipelineStage[] = pipelineRaw.map(p => ({
      ...p,
      pct: (p.count / maxCount) * 100,
    }));

    // ── Prazos críticos (7 dias) — consolida agenda + tenders com deadline próximo ──
    const in7d = new Date(now.getTime() + 7 * 86_400_000);
    const prazosFromAgenda: PrazoCritico[] = (agenda || [])
      .filter((a: any) => {
        const d = a.date ? new Date(a.date) : null;
        return d && d >= now && d <= in7d;
      })
      .map((a: any) => ({
        id: a.id || `${a.type}-${a.targetId}`,
        targetId: a.targetId,
        titulo: a.title || a.objeto || '(Sem título)',
        subtitulo: a.organ || a.subtitle || '',
        dataLimite: new Date(a.date),
        valor: toFiniteNumber(a.value),
        tipo: a.type || 'tender',
        route: a.route,
      }));

    // Fallback: se agenda não populou, pega direto de tenders
    const prazosFromTenders: PrazoCritico[] = prazosFromAgenda.length > 0 ? [] : tenders
      .filter(t => {
        const d = t.deadline ? new Date(t.deadline) : null;
        return d && d >= now && d <= in7d && !([TenderStatus.WON, TenderStatus.LOST, TenderStatus.NO_GO, TenderStatus.CLOSED] as TenderStatus[]).includes(t.status as TenderStatus);
      })
      .map(t => ({
        id: t.id,
        targetId: t.id,
        titulo: t.title || '(Sem título)',
        subtitulo: t.organ || '',
        dataLimite: new Date(t.deadline!),
        valor: toFiniteNumber(t.value),
        tipo: 'tender',
        route: '/ongoing',
      }));

    const prazosCriticos = [...prazosFromAgenda, ...prazosFromTenders]
      .sort((a, b) => a.dataLimite.getTime() - b.dataLimite.getTime())
      .slice(0, 5);

    // ── Insights IA (regras locais) ──
    const insights: InsightIA[] = [];

    // 1. Certidões vencendo: detecta via agenda com tipo 'document' vencendo em <30d
    const certidoesVencendo = (agenda || []).filter((a: any) => {
      if (a.type !== 'document' && a.type !== 'certification') return false;
      const d = a.date ? new Date(a.date) : null;
      return d && daysBetween(d, now) <= 30 && daysBetween(d, now) >= 0;
    });
    if (certidoesVencendo.length > 0) {
      insights.push({
        tone: 'warning',
        badgeLabel: 'Atenção',
        title: `${certidoesVencendo.length} certidão${certidoesVencendo.length > 1 ? 'ões' : ''} vence${certidoesVencendo.length > 1 ? 'm' : ''} em 30 dias`,
        text: 'Renove antes do vencimento para não perder habilitação em novos editais.',
        cta: 'Renovar',
        action: '/documents',
      });
    }

    // 2. Editais em altíssima prioridade
    const highPriority = tenders.filter(t => t.status === TenderStatus.EXTREME_PRIORITY || t.status === TenderStatus.HIGH_PRIORITY);
    if (highPriority.length > 0) {
      const first = highPriority[0];
      insights.push({
        tone: 'info',
        badgeLabel: 'Oportunidade',
        title: `${highPriority.length} edital${highPriority.length > 1 ? 'es' : ''} em alta prioridade`,
        text: `${first.title?.slice(0, 60) ?? ''}${(first.title?.length ?? 0) > 60 ? '…' : ''} — avalie se deve avançar.`,
        cta: 'Ver análise IA',
        action: '/ai-analysis',
      });
    }

    // 3. Contratos vencendo em 45d
    const contratosVencendo = contracts.filter(c => {
      if (!c.endDate) return false;
      const d = new Date(c.endDate);
      const days = daysBetween(d, now);
      return days >= 0 && days <= 45;
    });
    if (contratosVencendo.length > 0) {
      insights.push({
        tone: 'success',
        badgeLabel: 'Renovação',
        title: `${contratosVencendo.length} contrato${contratosVencendo.length > 1 ? 's' : ''} próximo${contratosVencendo.length > 1 ? 's' : ''} de renovação`,
        text: 'Histórico de entrega positivo. Prepare proposta de renovação com antecedência.',
        cta: 'Iniciar renovação',
        action: '/contracts',
      });
    }

    // Limita a 3 insights
    const topInsights = insights.slice(0, 3);

    // ── Atividade recente ──
    const atividade: AtividadeItem[] = (auditLogs || [])
      .slice(0, 8)
      .map((log: any, idx: number) => {
        const userName = log.userName || log.user?.name || 'Sistema';
        const isAI = /\b(IA|AI|Gemini|assistente)\b/i.test(userName) || log.isAI === true;
        const initials = isAI ? 'IA' : userName.split(' ').map((s: string) => s[0]).slice(0, 2).join('').toUpperCase();
        return {
          id: log.id || idx,
          userInitials: initials || '??',
          userName,
          isAI,
          action: log.action || log.description || 'atualizou',
          target: log.resourceLabel || log.resource || log.resourceType || '',
          createdAt: log.createdAt ? new Date(log.createdAt) : new Date(),
        };
      });

    return {
      kpis,
      pipeline,
      prazosCriticos,
      insights: topInsights,
      atividade,
      totalPrazos7d: prazosCriticos.length,
    };
  }, [tenders, contracts, agenda, auditLogs, performance]);

  const isEmpty = tenders.length === 0 && contracts.length === 0;

  return {
    data,
    tenders,
    contracts,
    isLoading,
    isEmpty,
    error,
    reload: load,
  };
}
